import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import {
  fetchJobSearchResults,
  fetchTerms,
  createJobSearchParams,
} from '~/index';
import getCoreServerSideProps from '../modules/core/getCoreServerSideProps';

const JobSearch = dynamic(
  () => import('../modules/job-search/containers/JobSearch'),
  {
    ssr: false,
  },
);
const CareerPageRenderer = dynamic(
  () =>
    import('~/modules/career-pages/helpers/CareerPageRenderer').then(
      (mod) => mod.CareerPageRenderer,
    ),
  {
    ssr: false,
  },
);
/**
 * Fetches terms for the job search page based on the specified language.
 *
 * @param {string} language - The language code for which the terms should be fetched.
 * @returns {ReturnType<typeof fetchTerms>} A promise that resolves to the fetched terms object.
 */
const getTerms = (language) => {
  return fetchTerms(language, { query: '?mode=JOB_SEARCH' });
};

/**
 * Fetches server-side props for the job search page.
 *
 * @param {object} ctx - The context object provided by Next.js.
 * @param {object} ctx.query - The query parameters from the request.
 * @param {string} ctx.cookie - The cookies from the request.
 * @param {string} ctx.locale - The locale of the request.
 * @returns {Promise<object>} An object containing props for the page, including:
 * - `pageContent` {object}: The content of the page.
 * - `siteContent` {object}: The content of the site.
 * - `context` {object}: The context of the request.
 * - `terms` {object}: The terms data fetched based on the language.
 * - `initialResults` {object}: The initial job search results.
 */
export const getServerSideProps = async (ctx) => {
  try {
    const { publicRuntimeConfig } = getConfig(); // Log here

    const { pageContent, siteContent, context } =
      await getCoreServerSideProps(ctx);

    const language = context.locale || publicRuntimeConfig.defaultLanguage;
    const terms = await getTerms(language);

    const searchParams = createJobSearchParams(
      language,
      ctx.query?.encodedParameters ?? '',
    );

    const initialResults = await fetchJobSearchResults(
      searchParams,
      ctx.req?.headers?.cookie,
    );

    return {
      props: { pageContent, siteContent, context, terms, initialResults },
    };
  } catch (error) {
    console.error('=== GSSP CRASH ===', {
      message: error.message,
      stack: error.stack,
      query: ctx?.query,
      hasReq: !!ctx?.req,
      cookieStart: ctx?.req?.headers?.cookie?.slice(0, 50),
    });
    return { notFound: true }; // 404 instead of 500
  }
};

/**
 * Renders the Job Search Next Page component.
 *
 * This component wraps the `JobSearch` component within a `CareerPageRenderer`
 * and provides additional props for rendering job search functionality.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.context - The context object containing locale and pathname.
 * @param {string} props.context.locale - The current language/locale.
 * @param {string | undefined} [props.context.pathname] - The current pathname of the page.
 * @param {PageContentProps} props.pageContent - The content data for the page.
 * @param {JobSearchResultType} props.initialResults - The initial results for the job search.
 * @param {Term[]} props.terms - The terms and conditions or other related data.
 *
 * @returns {React.ReactElement} The rendered Job Search Next Page component.
 */
function JobSearchNextPage(props) {
  const { publicRuntimeConfig } = getConfig(); // Log here
  const language = props.context.locale;
  return (
    <CareerPageRenderer {...props} withBreadcrumb={true} withFootShare={true}>
      <JobSearch
        type={props.initialResults.type || 'REGULAR'}
        filterCategories={props.pageContent.filterCategories}
        language={language}
        terms={props.terms}
        initialResults={props.initialResults}
        pathName={props?.context?.pathname ?? ''}
        authBaseUrl={publicRuntimeConfig.authBaseUrl ?? ''}
      />
    </CareerPageRenderer>
  );
}

export default JobSearchNextPage;
